export type DialerWidgetContainerId = 'left' | 'right';
export type DialerWidgetId =
  | 'voice_transcript'
  | 'action_items'
  | 'ai_summary'
  | 'conversation_highlights'
  | 'rag_suggestions';

export type DialerWidgetLayout = Record<DialerWidgetContainerId, DialerWidgetId[]>;

export const ALL_DIALER_WIDGET_IDS: DialerWidgetId[] = [
  'voice_transcript',
  'action_items',
  'ai_summary',
  'conversation_highlights',
  'rag_suggestions',
];

export const DEFAULT_WIDGET_LAYOUT: DialerWidgetLayout = {
  left: ['voice_transcript', 'action_items'],
  right: ['ai_summary', 'conversation_highlights', 'rag_suggestions'],
};

function isWidgetId(value: string): value is DialerWidgetId {
  return ALL_DIALER_WIDGET_IDS.includes(value as DialerWidgetId);
}

function arrayMove<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const copy = [...items];
  const [item] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, item);
  return copy;
}

export function normalizeWidgetLayout(
  raw: Partial<Record<DialerWidgetContainerId, string[]>> | null | undefined,
): DialerWidgetLayout {
  const next: DialerWidgetLayout = { left: [], right: [] };
  const seen = new Set<DialerWidgetId>();

  for (const containerId of ['left', 'right'] as DialerWidgetContainerId[]) {
    const values = raw?.[containerId] ?? [];
    for (const value of values) {
      if (!isWidgetId(value) || seen.has(value)) continue;
      seen.add(value);
      next[containerId].push(value);
    }
  }

  for (const widgetId of ALL_DIALER_WIDGET_IDS) {
    if (seen.has(widgetId)) continue;
    next.right.push(widgetId);
  }

  return next;
}

export function resolveWidgetContainer(
  layout: DialerWidgetLayout,
  value: string | null | undefined,
): DialerWidgetContainerId | null {
  if (!value) return null;
  if (value === 'left' || value === 'right') return value;
  if (!isWidgetId(value)) return null;

  if (layout.left.includes(value)) return 'left';
  if (layout.right.includes(value)) return 'right';
  return null;
}

export function moveWidgetInLayout(
  layout: DialerWidgetLayout,
  activeWidgetId: DialerWidgetId,
  overId: string | null | undefined,
): DialerWidgetLayout {
  const sourceContainer = resolveWidgetContainer(layout, activeWidgetId);
  const targetContainer = resolveWidgetContainer(layout, overId);

  if (!sourceContainer || !targetContainer) return layout;
  if (activeWidgetId === overId) return layout;

  if (sourceContainer === targetContainer) {
    const items = layout[sourceContainer];
    const oldIndex = items.indexOf(activeWidgetId);
    if (oldIndex < 0) return layout;

    const nextIndex =
      typeof overId === 'string' && isWidgetId(overId) ? items.indexOf(overId) : items.length - 1;
    if (nextIndex < 0 || nextIndex === oldIndex) return layout;

    return {
      ...layout,
      [sourceContainer]: arrayMove(items, oldIndex, nextIndex),
    };
  }

  const sourceItems = [...layout[sourceContainer]];
  const targetItems = [...layout[targetContainer]];
  const sourceIndex = sourceItems.indexOf(activeWidgetId);
  if (sourceIndex < 0) return layout;

  sourceItems.splice(sourceIndex, 1);

  const targetIndex =
    typeof overId === 'string' && isWidgetId(overId)
      ? Math.max(0, targetItems.indexOf(overId))
      : targetItems.length;
  targetItems.splice(targetIndex, 0, activeWidgetId);

  return {
    ...layout,
    [sourceContainer]: sourceItems,
    [targetContainer]: targetItems,
  };
}
